import { Experiment } from '@naturalcycles/shared'
import { SupportedLocale } from '@src/cnst/translations.cnst'
import {
  PricingCompactCard,
  PricingCompactCardProps,
} from '@src/components/ab-397/pricing-compact-card/PricingCompactCard.component'
import {
  handlePathAlternateLinksProps,
  mapAlternatesToLinks,
} from '@src/components/alternate-links/AlternateLinks.component'
import { ClientOnly } from '@src/components/client-only/ClientOnly.component'
import { Disclaimer, DisclaimerProps } from '@src/components/disclaimer/Disclaimer.component'
import {
  EffectivenessSectionBlocks,
  EffectivenessSectionBlocksProps,
} from '@src/components/effectiveness-section-blocks/EffectivenessSectionBlocks.component'
import { FactQuoteBanner } from '@src/components/fact-quote-banner/FactQuoteBanner.component'
import { FaqsSection, FaqsSectionProps } from '@src/components/faqs-section/FaqsSection.component'
import { HeroBadges, HeroBadgesProps } from '@src/components/hero-badges/HeroBadges.component'
import {
  HomepageHero,
  HomepageHeroHead,
} from '@src/components/homepage-hero/HomepageHero.component'
import {
  HowItWorksNc,
  HowItWorksNcProps,
} from '@src/components/how-it-works-nc/HowItWorksNc.component'
import { InTheApp, InTheAppProps } from '@src/components/in-the-app/InTheApp.component'
import {
  InstagramSection,
  InstagramSectionProps,
} from '@src/components/instagram-section/InstagramSection.component'
import { Page } from '@src/components/page/Page.component'
import {
  PopupHomepage,
  PopupHomepageProps,
} from '@src/components/popup-homepage/PopupHomepage.component'
import { QuizIntro, QuizIntroProps } from '@src/components/quiz-intro/QuizIntro.component'
import { Seo, SeoMetaTag, SeoProps } from '@src/components/seo/Seo.component'
import {
  PageTypeTrailingButton,
  TrailingButton,
} from '@src/components/trailing-button/TrailingButton.component'
import { WideReviews, WideReviewsProps } from '@src/components/wide-reviews/WideReviews.component'
import { languageContext } from '@src/context/language.context'
import { MigratedPageProps } from '@src/helpers/gatsby/pages'
import { useAppSelector } from '@src/hooks/useAppSelector'
import { Breakpoints } from '@src/hooks/useBreakpoint'
import { useExperiment } from '@src/hooks/useExperiment'
import { useWindowDimensions } from '@src/hooks/useWindowDimensions'
import {
  GeolocationCountry,
  selectGeolocationCountry,
} from '@src/store/geolocation/geolocation.slice'
import { DatoCmsFileField, DatoCmsMedia, DatoCmsMediaAsset } from 'dato-cms-assets'
import { type ReactNode, useContext, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'

interface HomepageProps
  extends MigratedPageProps<{
    homepage: {
      _seoMetaTags: SeoMetaTag[]
      seo: Omit<SeoProps, '_seoMetaTags'>
      heroHeading: string
      heroContext: string
      heroCta: string
      footnote: string
      ratings: HeroBadgesProps['ratings']
      bannerBadges: HeroBadgesProps['bannerBadges']
      mobileHeroMedia: DatoCmsMediaAsset
      desktopHeroMedia: DatoCmsMediaAsset
      fertilityGoal: {
        title: string
        subtitle: string
        imagetext: string
        backgroundImage: DatoCmsFileField
        goalButtons: QuizIntroProps['buttonTexts']
      }
      sectionHeading: string
      howDoesItWork: HowItWorksNcProps['steps']
      effectivenessComparison: EffectivenessSectionBlocksProps['effectivenessComparison']
      mobileEffectivenessBackground: { media: DatoCmsFileField }
      desktopEffectivenessBackground: { media: DatoCmsFileField }
      bannerQuote: string
      bannerCta: string
      inTheApp: {
        sectionHeader: string
        inTheAppItems: InTheAppProps['appInfoSections']
      }
      reviewsTitle: string
      reviewsSection: WideReviewsProps['reviews']
      reviewsSectionCta: [WideReviewsProps['reviewsCta']]
      instagramSection: Omit<InstagramSectionProps, 'buttonCta'> & { buttoncta: string }
      faqSection: FaqsSectionProps
      disclaimer: DisclaimerProps
      trailingButton: { text: string }
    }
    popups: PopupHomepageProps['popups']
    instagram: InstagramSectionProps['instagram']
  }> {}

export default function Homepage({ pageContext }: HomepageProps): ReactNode {
  const { homepage, popups, instagram } = pageContext
  const content = homepage
  const { width } = useWindowDimensions()
  const isMobile = Boolean(width && width < Breakpoints.bpMedium)

  const { locale } = useContext(languageContext)
  const isUSLocale = locale === SupportedLocale.enUS

  const country = useAppSelector(selectGeolocationCountry)

  // Our attentive popups are shown based on geolocation and we don't want to clash with them
  const isUSGeolocation = country === GeolocationCountry.US
  const isSEGeolocation = country === GeolocationCountry.SE
  const isCAGeolocation = country === GeolocationCountry.CA

  useExperiment(Experiment.HCP_VISIBILITY)

  const selectedMedia = useMemo((): DatoCmsMedia | null => {
    return isMobile ? content.mobileHeroMedia.media : content.desktopHeroMedia.media
  }, [content])

  const allHeroMedias = [content.mobileHeroMedia.media, content.desktopHeroMedia.media]

  const { alternates, xDefaultLocale } = handlePathAlternateLinksProps({ path: '' })
  const links = mapAlternatesToLinks(alternates, xDefaultLocale)

  const pricingCompactCardProps: PricingCompactCardProps = {
    savings: 'Save up to 55%',
    condition: 'with NC° Annual Plan',
  }
  const [AB397] = useExperiment(Experiment.PERCEIVED_PRICE, [SupportedLocale.enUS])

  return (
    <Page pageContext={pageContext}>
      <HomepageHeroHead medias={allHeroMedias} />
      <Helmet>{links}</Helmet>
      <Seo {...content.seo} _seoMetaTags={content._seoMetaTags} />
      <HomepageHero
        heading={content.heroHeading}
        context={content.heroContext}
        cta={content.heroCta}
        media={selectedMedia}
        footnote={content.footnote}
        bucket397={AB397 as 'test' | 'control' | null}
      />

      <HeroBadges ratings={content.ratings} bannerBadges={content.bannerBadges} />
      <ClientOnly>
        {AB397 === 'test' && (
          <PricingCompactCard {...pricingCompactCardProps} hideOnDesktop hasBackground />
        )}
      </ClientOnly>
      <QuizIntro
        title={content.fertilityGoal.title}
        subTitle={content.fertilityGoal.subtitle}
        imageText={content.fertilityGoal.imagetext}
        image={content.fertilityGoal.backgroundImage}
        buttonTexts={content.fertilityGoal.goalButtons}
        utmSourceWeb="homepage_quiz_intro"
      />
      <HowItWorksNc sectionHeading={content.sectionHeading} steps={content.howDoesItWork} />
      <EffectivenessSectionBlocks
        effectivenessComparison={content.effectivenessComparison}
        mobileEffectivenessBackground={content.mobileEffectivenessBackground}
        desktopEffectivenessBackground={content.desktopEffectivenessBackground}
      />
      <FactQuoteBanner theme="purple" text={content.bannerQuote} cta={content.bannerCta} />
      <InTheApp
        sectionHeading={content.inTheApp.sectionHeader}
        appInfoSections={content.inTheApp.inTheAppItems}
      />
      <WideReviews
        heading={content.reviewsTitle}
        reviews={content.reviewsSection}
        reviewsCta={content.reviewsSectionCta[0]}
        loading="lazy"
      />
      <InstagramSection
        title={content.instagramSection.title}
        hashtag={content.instagramSection.hashtag}
        buttonCta={content.instagramSection.buttoncta}
        instagram={instagram}
      />
      <FaqsSection
        heading={content.faqSection.heading}
        questionAnswers={content.faqSection.questionAnswers}
        theme="lightGrey"
      />
      <ClientOnly>
        {!isUSGeolocation && !isSEGeolocation && !isCAGeolocation && isUSLocale && (
          <PopupHomepage popups={popups} />
        )}
      </ClientOnly>
      <TrailingButton button={content.trailingButton.text} page={PageTypeTrailingButton.Homepage} />
      <Disclaimer {...content.disclaimer} />
    </Page>
  )
}
